// Twin Fleet — every farm's twin on one page (plan Phase 6).
//
// Mirrors FleetAssurance's shape: summary tiles + a worklist table, sorted
// most-behind first by the server. Each row leads with the two gaps the Twin
// page leads with (value created vs base; still on the table vs best), plus
// this week's checks, the twin exception backlog, and grass headroom.
// Farms without a twin yet are listed honestly at the bottom, never hidden.
//
// Server contract: GET /twin/fleet (routes/twin.py). Visibility is
// membership-based server-side — sysadmins and assurance officers see the
// whole estate; a multi-farm owner sees exactly their farms.

import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, CheckCircle2, GitCompareArrows, Leaf } from 'lucide-react'
import clsx from 'clsx'
import { getTwinFleet } from '../../services/api'

const t = (kg) => `${(kg / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })} t`
const ugxM = (v) => {
  const sign = v < 0 ? '−' : ''
  const a = Math.abs(v)
  if (a >= 1e9) return `${sign}UGX ${(a / 1e9).toFixed(2)} bn`
  return `${sign}UGX ${(a / 1e6).toFixed(1)} M`
}

function SummaryCard({ label, value, icon: Icon, tone = 'gray' }) {
  const tones = { gray: 'text-gray-500', amber: 'text-amber-600', green: 'text-green-600' }
  return (
    <div className="card p-4">
      <div className="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
        {Icon && <Icon className={clsx('h-4 w-4', tones[tone])} />}
        {label}
      </div>
      <div className="mt-1 text-2xl font-bold text-gray-900">{value}</div>
    </div>
  )
}

export default function TwinFleet() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['twinFleet'],
    queryFn: ({ signal }) => getTwinFleet({ signal }),
  })

  const ranches = data?.ranches || []
  const behindFarms = ranches.filter((r) => (r.checks_behind || 0) > 0).length
  const totalCreatedKg = ranches.reduce((s, r) => s + (r.value_created_kg || 0), 0)

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/twin" className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="h-5 w-5 text-gray-500" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <GitCompareArrows className="h-6 w-6 text-primary-600" />
            Twin Fleet
          </h1>
          <p className="text-gray-500 mt-1">
            Every farm's three trajectories at a glance, ranked by which farm
            most needs a look this week. Farms without a twin yet are listed,
            not hidden.
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
          {error.message || 'Failed to load the twin fleet.'}
        </div>
      )}

      {!error && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <SummaryCard label="Farms twinned" value={`${data?.twinned ?? 0} / ${ranches.length}`} icon={GitCompareArrows} />
          <SummaryCard label="Farms behind trajectory" value={behindFarms} icon={AlertTriangle}
            tone={behindFarms > 0 ? 'amber' : 'gray'} />
          <SummaryCard label="Value created, fleet-wide" value={t(totalCreatedKg)} icon={CheckCircle2}
            tone={totalCreatedKg > 0 ? 'green' : 'gray'} />
        </div>
      )}

      <div className="card overflow-hidden">
        {isLoading ? (
          <p className="p-6 text-gray-500">Loading the fleet…</p>
        ) : ranches.length === 0 ? (
          <p className="p-6 text-gray-500">No ranches visible to your account.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Farm</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Value created</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Still on the table</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Head</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <span className="inline-flex items-center gap-1"><Leaf className="h-3.5 w-3.5" />Grass headroom</span>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">This week's checks</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Open twin flags</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {ranches.map((r) => (
                  <tr key={r.ranch_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{r.name || r.code || '—'}</div>
                      <div className="text-xs text-gray-400">
                        {r.code}{r.as_of ? ` · twin run ${new Date(r.as_of).toLocaleDateString()}` : ''}
                      </div>
                    </td>
                    {r.has_twin ? (
                      <>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                          <div className={clsx('font-medium', (r.value_created_kg || 0) > 0 ? 'text-green-700' : 'text-gray-900')}>
                            {r.value_created_kg != null ? t(r.value_created_kg) : '—'}
                          </div>
                          {r.value_created_ugx != null && <div className="text-xs text-gray-400">{ugxM(r.value_created_ugx)}</div>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                          <div className="font-medium text-gray-900">{r.still_on_table_kg != null ? t(r.still_on_table_kg) : '—'}</div>
                          {r.still_on_table_ugx != null && <div className="text-xs text-gray-400">{ugxM(r.still_on_table_ugx)}</div>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                          {r.head != null ? Math.round(r.head).toLocaleString() : '—'}
                        </td>
                        <td className={clsx('px-6 py-4 whitespace-nowrap text-right text-sm font-medium',
                          r.grazing_headroom_pct == null ? 'text-gray-300'
                            : r.grazing_headroom_pct < 0 ? 'text-amber-700' : 'text-gray-900')}>
                          {r.grazing_headroom_pct != null ? `${Math.round(r.grazing_headroom_pct)}%` : 'not measured'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {(r.checks_behind || 0) > 0 ? (
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800">
                              {r.checks_behind} of {r.checks_total} behind
                            </span>
                          ) : (
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                              on trajectory
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                          <span className={clsx('font-medium', (r.open_twin_exceptions || 0) > 0 ? 'text-amber-700' : 'text-gray-300')}>
                            {r.open_twin_exceptions || 0}
                          </span>
                        </td>
                      </>
                    ) : (
                      <td colSpan={6} className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                        Not twinned yet — seed its assumptions register to bring it onto this page.
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
